import api from './client';
import {
  Rating,
  RatingStats,
  CreateRatingInput,
  TopRatedUser,
  RatingReply,
  CommissionerRating,
  CommissionerRatingStats,
  CreateCommissionerRatingInput,
} from '../types';

export const ratingsApi = {
  // Create a rating
  async createRating(input: CreateRatingInput): Promise<Rating> {
    const { data } = await api.post<Rating>('/ratings', input);
    return data;
  },

  // Update a rating
  async updateRating(ratingId: string, input: Partial<CreateRatingInput>): Promise<Rating> {
    const { data } = await api.put<Rating>(`/ratings/${ratingId}`, input);
    return data;
  },

  // Delete a rating
  async deleteRating(ratingId: string): Promise<void> {
    await api.delete(`/ratings/${ratingId}`);
  },

  // Get ratings for a sleeper account
  async getAccountRatings(
    sleeperAccountId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<{ ratings: Rating[]; stats: RatingStats | null }> {
    const { data } = await api.get(`/ratings/account/${sleeperAccountId}`, {
      params: options,
    });
    return data;
  },

  // Get ratings by current user
  async getMyRatings(options?: { limit?: number; offset?: number }): Promise<Rating[]> {
    const { data } = await api.get<Rating[]>('/ratings/my-ratings', {
      params: options,
    });
    return data;
  },

  // Check if user can rate another user
  async canRate(sleeperAccountId: string): Promise<{
    canRate: boolean;
    sharedLeagues: string[];
  }> {
    const { data } = await api.get(`/ratings/can-rate/${sleeperAccountId}`);
    return data;
  },

  // Get top rated users (overall_average >= 3.5 by server default).
  async getTopRated(limit?: number): Promise<TopRatedUser[]> {
    const { data } = await api.get<TopRatedUser[]>('/ratings/top-rated', {
      params: { limit },
    });
    return data;
  },

  // Get worst-rated users for the "Top Avoids" leaderboard
  // (overall_average <= 2.5 by server default). Same payload shape as
  // getTopRated so the same TopRatedUser type works for both.
  async getTopAvoids(limit?: number): Promise<TopRatedUser[]> {
    const { data } = await api.get<TopRatedUser[]>('/ratings/top-avoids', {
      params: { limit },
    });
    return data;
  },

  // Add a reply to a rating
  async addReply(ratingId: string, replyText: string): Promise<RatingReply> {
    const { data } = await api.post<RatingReply>(`/ratings/${ratingId}/replies`, {
      reply_text: replyText,
    });
    return data;
  },

  // Delete a reply
  async deleteReply(replyId: string): Promise<void> {
    await api.delete(`/ratings/replies/${replyId}`);
  },

  // ── Commissioner ratings ──────────────────────────────────────────

  // Check eligibility and get the list of leagues where the rated user
  // was the commissioner and the rater was a member.
  async canRateCommissioner(sleeperAccountId: string): Promise<{
    canRate: boolean;
    sharedCommishLeagues: Array<{ id: string; name: string; season: string }>;
    reason?: string;
  }> {
    const { data } = await api.get(
      `/ratings/commissioner/can-rate/${sleeperAccountId}`
    );
    return data;
  },

  // Get commissioner ratings (and aggregate stats) for a sleeper account.
  async getCommissionerRatings(
    sleeperAccountId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<{ ratings: CommissionerRating[]; stats: CommissionerRatingStats | null }> {
    const { data } = await api.get(
      `/ratings/commissioner/account/${sleeperAccountId}`,
      { params: options }
    );
    return data;
  },

  async createCommissionerRating(
    input: CreateCommissionerRatingInput
  ): Promise<CommissionerRating> {
    const { data } = await api.post<CommissionerRating>(
      '/ratings/commissioner',
      input
    );
    return data;
  },

  async updateCommissionerRating(
    ratingId: string,
    input: Partial<CreateCommissionerRatingInput>
  ): Promise<CommissionerRating> {
    const { data } = await api.put<CommissionerRating>(
      `/ratings/commissioner/${ratingId}`,
      input
    );
    return data;
  },

  async deleteCommissionerRating(ratingId: string): Promise<void> {
    await api.delete(`/ratings/commissioner/${ratingId}`);
  },
};

